import Accordion from "@/components/common/Accordion";
import BaseCard from "@/components/common/BaseCard";
import { appThemeStyle } from "@/constants/theme.config";
import { Skeleton } from "antd-mobile";
import useDayjs from "@/hooks/useDayjs";
import useDateFormat from "@/hooks/useDateFormat";
import useTranslate from "@/hooks/useTranslate";
import { useAdCpt2 } from "@/hooks/adCpt/daily";
import { is_en_gl_link, is_es_mx_link, is_tr_link } from "@/i18n/lang";
import { pxChangeRem } from "@/utils";
import { calcByUiRatio } from "@/constants/runtime.config";
import classNames from "classnames";
import { commonStateAtom } from "@/atoms/common";
import { dayListAtom } from "@/atoms/daily";
import { homeAtom } from "@/atoms/home";
import { isLongText } from "@/utils/isLongText";
import { getWeekName } from "@/utils/getWeekName";
import { refreshIdAtom } from "@/atoms/refreshId";
import TestTips1 from "@/components/TestTips1";

const SKELETON_LARGE_STYLE = {
  "--width": "100%",
  "--height": pxChangeRem(390),
  "--border-radius": "10px",
};

const SKELETON_SMALL_STYLE = {
  "--width": "100%",
  "--height": pxChangeRem("55px"),
  "--border-radius": "10px",
};

const SKELETON_ITEMS_COUNT = 9;
const DailyDefault: React.FC = () => {
  const locInfo = LocalStorage.getItem("INFO");
  const { dayjs, userTimeZone } = useDayjs();
  const navigate = useNavigate();
  const dailyData = useAtomValue(commonStateAtom);
  const weatherStatus = dailyData?.data?.info?.weatherStatus || locInfo?.weatherStatus;
  const tipsStyle = appThemeStyle?.[weatherStatus]?.tipsStyle ?? {};
  const [dayList, setDayList] = useAtom(dayListAtom);
  const refreshId = useAtomValue(refreshIdAtom);
  const { shortDate } = useDateFormat();
  const { $t } = useTranslate();
  const { state } = useLocation();
  const { pathname } = useLocation();
  const [, fetchDailyData] = useAtom(homeAtom);
  useEffect(() => {
    fetchDailyData(pathname);
  }, []);
  const handleSearchDetail = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`detail/${index}`, {
      replace: false,
      state: { index, dayList },
    });
  };

  const getDayOrNight = (item: any, isDayTime: number) => {
    return item[isDayTime === 1 ? "Day" : "Night"];
  };

  const getFormattedDate = (timestamp: number) => {
    if (is_en_gl_link() || is_es_mx_link()) {
      return dayjs.unix(timestamp).tz(userTimeZone).format("DD/MM");
    }
    const dateStr = dayjs.unix(timestamp).tz(userTimeZone).format(shortDate);
    return isLongText() ? dateStr.replace(/\//g, ".") : dateStr;
  };

  // 在组件内部添加计算 expandIndex 的逻辑
  const getExpandIndex = () => {
    // 检查 state 是否存在且 dayList 已加载
    if (!state || !dayList || dayList.length === 0) return 0;

    // 查找匹配的索引
    const matchedIndex = dayList.findIndex((item: any) => {
      // 检查 EpochDateTime 是否匹配
      if (item.EpochDateTime === state.EpochDateTime) {
        // 如果有用户时区，则比较格式化后的日期
        if (userTimeZone) {
          const formattedDate = getFormattedDate(item.EpochDateTime);
          const stateFormattedDate = getFormattedDate(state.EpochDateTime);
          return formattedDate === stateFormattedDate;
        } else {
          // 否则直接比较 DateShort
          return item.DateShort === state.DateShort;
        }
      }
      return false;
    });

    // 如果找到匹配项，返回其索引；否则默认返回 0
    return matchedIndex >= 0 ? matchedIndex : 0;
  };

  // 使用该函数来获取初始展开索引
  const expandIndex = getExpandIndex();
  const renderTitle = ({ item, index, handleClick }: any) => {
    const weekDayName = getWeekName(item.EpochDateTime);
    const dayOrNight = getDayOrNight(item, item.isDayTime);
    const DailyItemTitle = () => {
      const byRatio = calcByUiRatio();
      let columnFlexRatios = {
        1: 1,
        3: 4,
        4: 2,
        5: 1.5,
        6: 1,
      };

      return (
        <div
          className={classNames(["flex justify-between items-center gap-x-10 text-12"])}
          onClick={() => handleClick(item, index)}
        >
          {/* 第1列*/}
          <div
            className={classNames(["text-10 text-center"])}
            style={{
              flex: columnFlexRatios[1],
            }}
          >
            <p>{weekDayName ?? item.Week}</p>
            <p>{item.EpochDateTime && (userTimeZone ? getFormattedDate(item.EpochDateTime) : item.DateShort)}</p>
          </div>
          {/* 第2列 */}
          <div className={classNames(["w-20"])}>
            <WeatherImage className="w-full" src={dayOrNight?.WeatherIconImg} />
          </div>
          {/* 第3列 */}
          <div
            className="w-120"
            style={{
              flex: columnFlexRatios[3],
            }}
          >
            {dayOrNight.WeatherText}
          </div>
          {/* 第4列 */}
          <div
            className={classNames(["text-16"])}
            style={{
              flex: columnFlexRatios[4],
            }}
          >
            {item?.Temperature?.Maximum?.Value}
            {item?.Temperature?.Maximum?.Unit}/{item?.Temperature?.Minimum?.Value}
            {item?.Temperature?.Minimum?.Unit}
          </div>
          {/* 第5列 */}
          <div
            className="flex items-center gap-x-5"
            style={{
              flex: columnFlexRatios[5],
            }}
          >
            <RainProbSvg width={12 * byRatio} />
            <div>
              {is_tr_link() ? "%" + dayOrNight.PrecipitationProbability : dayOrNight.PrecipitationProbability + "%"}
            </div>
          </div>
          {/* 第6列 */}
          <div
            className={classNames(["flex items-center justify-center"])}
            style={{
              flex: columnFlexRatios[6],
            }}
          >
            <SvgIcon size={20 * byRatio} name={item?.isExpand ? "svg05" : "svg01"} />
          </div>
        </div>
      );
    };

    return <DailyItemTitle />;
  };
  const WeatherDetails = ({ item, dayOrNight }: any) => {
    return (
      <>
        {dayOrNight?.LongPhrase && (
          <div
            className={classNames([
              "rounded-8 color-[var(--app-text-14)] p-8 box-border font-weight-300 text-10 lh-13",
            ])}
            style={{
              backgroundColor: "var(--app-bg-11)",
              ...tipsStyle,
            }}
          >
            {dayOrNight?.LongPhrase}
          </div>
        )}
        {item?.warnings?.map((warningItem: any, warningIndex: number) => {
          const warning =
            appThemeStyle?.[weatherStatus]?.warning?.[warningItem?.Color?.Name?.toLocaleLowerCase()] ?? {};
          return (
            <EarlyWarningView
              key={warningIndex}
              title={warningItem.Description?.Localized}
              content={`${warningItem?.detail?.start}-${warningItem?.detail?.end}`}
              onClick={() => {}}
              color={warning?.fontColor}
              bgColor={tipsStyle?.backgroundColor}
              fillColor={warning?.fillColor}
              AlertID={warningItem?.AlertID}
            />
          );
        })}
      </>
    );
  };

  const renderContent = ({ item, index: dayListIndex }: any) => {
    if (!item?.Items?.length) {
      return null;
    }
    const dayOrNight = getDayOrNight(item, item.isDayTime);

    return (
      <div className="flex flex-col gap-y-8">
        <WeatherDetails dayOrNight={dayOrNight} item={item} />
        <BaseCard itemList={item.Items} useTransparentStyle={true} />
        <div className="center" onClick={event => handleSearchDetail(dayListIndex, event)}>
          <div>{$t("title05")}</div>
          <SvgIcon name="svg03" />
        </div>
      </div>
    );
  };

  const renderSkeletons = () => (
    <>
      <Skeleton animated style={SKELETON_LARGE_STYLE} />
      {Array.from({ length: SKELETON_ITEMS_COUNT }).map((_, index) => (
        <Skeleton key={`skeleton-${index}`} animated style={SKELETON_SMALL_STYLE} />
      ))}
    </>
  );

  const renderAccordion = () => {
    return (
      <Accordion
        key={refreshId}
        accordionList={dayList}
        setLocalList={setDayList}
        renderTitle={renderTitle}
        renderContent={renderContent}
        expandIndex={expandIndex}
        isEnableScroll={state?.EpochDateTime}
      />
    );
  };

  try {
    return (
      <div className={classNames(["flex flex-col justify-start gap-y-10 box-border p-[0_16px]"])}>
        <TestTips1 />
        <div data-position="position-24">{useAdCpt2("position-24", pathname)}</div>
        <div data-position="position-09">{useAdCpt2("position-09", pathname)}</div>
        {isEmptyType(dayList) ? renderSkeletons() : renderAccordion()}
      </div>
    );
  } catch (error: any) {
    // handleError(error, "DailyMain/Default/index.tsx", "");
    // return null;
  }
};

export default DailyDefault;
